import React, { useState, useEffect } from 'react';
import {
    RiAddLine,
    RiDeleteBinLine,
    RiImageLine,
    RiLoader4Line,
    RiEditLine,
    RiCloseLine,
    RiSearchLine,
    RiLeafLine
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../config';

const ManageGenera = () => {
    const [genera, setGenera] = useState([]);
    const [families, setFamilies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);

    const initialFormState = {
        family_id: '',
        name: '',
        common_name: '',
        description: '',
        image_url: '',
        is_accepted: 1
    };

    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchGenera();
        fetchFamilies();
    }, []);

    const fetchFamilies = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/families/read.php`);
            const data = await response.json();
            if (response.ok) {
                setFamilies(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching families:', error);
        }
    };

    const fetchGenera = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/genera/read.php`);
            const data = await response.json();
            if (response.ok) {
                setGenera(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching genera:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_BASE_URL}/genera/update.php`
            : `${API_BASE_URL}/genera/create.php`;

        const payload = editingId
            ? { ...formData, id: editingId }
            : formData;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setFormData(initialFormState);
                setEditingId(null);
                fetchGenera();
            } else {
                alert(`Failed to ${editingId ? 'update' : 'create'} genus`);
            }
        } catch (error) {
            console.error('Error saving genus:', error);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormData({
            family_id: item.family_id,
            name: item.name,
            common_name: item.common_name,
            description: item.description,
            image_url: item.image_url,
            is_accepted: item.is_accepted
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure? This will delete the genus.')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/genera/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchGenera();
            }
        } catch (error) {
            console.error('Error deleting genus:', error);
        }
    };

    const filteredGenera = genera.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.common_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.family_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-[1400px] mx-auto">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tight">Taxonomic Genera</h2>
                    <p className="text-slate-500 font-medium mt-2">Manage biological genus classifications</p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                {/* Form Pane */}
                <div className="xl:col-span-4">
                    <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 p-8 sticky top-10">
                        <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                            {editingId ? <RiEditLine className="text-emerald-500" /> : <RiAddLine className="text-emerald-500" />}
                            {editingId ? 'Edit Genus' : 'Add New Genus'}
                        </h3>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Parent Family</label>
                                <select
                                    value={formData.family_id}
                                    onChange={e => setFormData({ ...formData, family_id: e.target.value })}
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                    required
                                >
                                    <option value="">Select Family...</option>
                                    {families.map(f => (
                                        <option key={f.id} value={f.id}>{f.name} ({f.common_name})</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Genus Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g. Panthera"
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500 italic"
                                    required
                                />
                            </div>

                            {/* <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Common Name</label>
                                <input
                                    type="text"
                                    value={formData.common_name}
                                    onChange={e => setFormData({ ...formData, common_name: e.target.value })}
                                    placeholder="e.g. Big Cats"
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                />
                            </div> */}

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Cover Image URL</label>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={formData.image_url}
                                        onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                                        placeholder="https://..."
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                    />
                                    {formData.image_url && <img src={formData.image_url} alt="preview" className="w-14 h-14 rounded-xl object-cover bg-slate-100" />}
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-medium h-32 focus:ring-2 focus:ring-emerald-500 outline-none"
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button className={`flex-1 font-black py-4 rounded-xl text-xs uppercase tracking-[0.2em] transition-all shadow-xl active:scale-95 text-white ${editingId ? 'bg-amber-500 shadow-amber-900/10' : 'bg-emerald-600 shadow-emerald-900/10'}`}>
                                    {editingId ? 'Update Genus' : 'Create Genus'}
                                </button>
                                {editingId && (
                                    <button type="button" onClick={() => {
                                        setEditingId(null);
                                        setFormData(initialFormState);
                                    }} className="px-6 bg-slate-100 text-slate-400 rounded-xl hover:bg-slate-200">
                                        <RiCloseLine size={24} />
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {/* List Pane */}
                <div className="xl:col-span-8">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="relative group flex-1">
                            <RiSearchLine className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search genera..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-100 rounded-full text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none shadow-sm transition-all"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20 text-emerald-500">
                            <RiLoader4Line className="animate-spin text-4xl" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {filteredGenera.map((item) => (
                                <div key={item.id} className="bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-lg transition-all">
                                    <div className="w-20 h-20 bg-slate-100 rounded-2xl overflow-hidden shrink-0">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <RiLeafLine size={24} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 px-2 py-1 rounded-md mb-2 inline-block">
                                                    {item.family_name || 'Unknown Family'}
                                                </span>
                                                <h4 className="font-bold text-slate-900 text-lg leading-none mb-1">{item.name}</h4>
                                                <p className="text-slate-400 text-xs font-serif italic">{item.common_name}</p>
                                            </div>
                                            <div className="flex gap-1">
                                                <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-emerald-600 bg-slate-50 hover:bg-emerald-50 rounded-lg transition-all">
                                                    <RiEditLine size={16} />
                                                </button>
                                                <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded-lg transition-all">
                                                    <RiDeleteBinLine size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageGenera;
